<template>
  <v-dialog v-model="visible" max-width="700px">
    <v-card class="d-flex flex-column" style="height: 80vh;">
      <v-card-title class="headline">
        Activity Levels - {{ title }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-5 overflow-y-auto">
          <!-- Date Filter Component -->
          <DateFilterComponent
            :range-key="rangeKey"
            :range-start="rangeStart"
            :range-end="rangeEnd"
            :loading="loading"
            @range-change="handleRangeChange"
            @date-range-change="handleDateRangeChange"
          />

          <!-- Summary widget: total count + pie chart -->
          <v-card class="mb-4" elevation="2" outlined>
            <v-card-text class="pt-3 pb-2">
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-chart-donut</v-icon>
                <span class="subtitle-2">Activity Summary</span>
                <v-spacer></v-spacer>
              </div>
              <v-skeleton-loader
                v-if="loading"
                type="image, list-item-two-line"
                class="mx-auto"
              ></v-skeleton-loader>
              <v-row v-else align="center">
                <v-col cols="7" md="8">
                  <apexchart type="donut" :options="pieChartOptions" :series="pieSeries" height="220" />
                </v-col>
                <v-col cols="5" md="4" class="text-center d-flex flex-column">
                  <div class="display-1 font-weight-bold">{{ Number(totalActivityCount).toLocaleString() }}</div>
                  <div class="caption grey--text text--darken-1 mt-1">Total Activities</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Tabs: By Module / By User in a compact card -->
          <v-card class="mb-4" elevation="1" outlined>
            <v-card-text class="py-1 px-2">
              <v-tabs v-model="tab" background-color="transparent" dense height="32">
                <v-tab class="caption">
                  <v-icon x-small class="mr-1">mdi-view-grid-outline</v-icon>
                  By Module
                </v-tab>
                <v-tab class="caption">
                  <v-icon x-small class="mr-1">mdi-account-multiple-outline</v-icon>
                  By User
                </v-tab>
              </v-tabs>
            </v-card-text>
          </v-card>

          <v-tabs-items v-model="tab">
            <!-- By Module Tab -->
            <v-tab-item>
              <v-skeleton-loader
                v-if="loading"
                type="list-item, list-item-avatar-three-line, list-item-avatar-three-line"
                class="mt-2"
              ></v-skeleton-loader>
              <template v-else>
                <div v-if="!modules || !modules.length" class="text-center grey--text">
                  No activity data available.
                </div>
                <template v-else>
                  <div v-for="m in modules" :key="m.key || m.label" class="mb-3">
                    <div class="d-flex align-center mb-1">
                      <v-icon small class="mr-2">{{ m.icon || 'mdi-chart-bar' }}</v-icon>
                      <span class="subtitle-2">{{ m.label || m.key || 'Module' }}</span>
                      <v-spacer></v-spacer>
                      <v-chip
                        v-if="isFiniteNumber(m.count)"
                        small
                        label
                        class="mr-2 font-weight-medium"
                      >
                        {{ m.count }}
                      </v-chip>
                      <span v-if="isFiniteNumber(m.level)" class="caption grey--text">{{ Math.round(m.level) }}%</span>
                    </div>
                    <ActivitySparkline :series="getSeries(m)" :color="getColor(m)" :categories="chartCategories" :height="100" />
                  </div>
                </template>
              </template>
            </v-tab-item>

            <!-- By User Tab -->
            <v-tab-item>
              <v-skeleton-loader
                v-if="loading"
                type="list-item, list-item-avatar-three-line, image, list-item-avatar-three-line"
                class="mt-2"
              ></v-skeleton-loader>
              <template v-else>
                <div v-if="!users || !users.length" class="text-center grey--text">
                  No user activity data available.
                </div>
                <template v-else>
                  <div v-for="u in users" :key="u.key || u.id || u.name" class="mb-4">
                    <div class="d-flex align-center mb-2">
                      <v-icon small class="mr-2">mdi-account</v-icon>
                      <span class="subtitle-2">{{ u.name || u.label || u.key || 'User' }}</span>
                      <v-spacer></v-spacer>
                      <v-chip v-if="isFiniteNumber(u.count)" small label class="mr-2 font-weight-medium">{{ u.count }}</v-chip>
                      <span v-if="isFiniteNumber(u.level)" class="caption grey--text">{{ Math.round(u.level) }}%</span>
                    </div>
                    <v-row>
                      <v-col cols="7" md="8">
                        <ActivitySparkline :series="getUserSeries(u)" :color="getColor(u)" :categories="chartCategories" :height="100" />
                      </v-col>
                      <v-col cols="5" md="4" class="text-center">
                        <apexchart type="donut" :options="getUserPieOptions(u)" :series="getUserPieSeries(u)" height="150" />
                      </v-col>
                    </v-row>
                  </div>
                </template>
              </template>
            </v-tab-item>
          </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="close()">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ActivitySparkline from '@/components/ActivitySparkline.vue';
import service from '@Company/service/module-activitylog.service';
import ApexCharts from 'vue-apexcharts';
import DateFilterComponent from './DateFilterComponent.vue';
import {
  normalizeModuleKey as normalizeModuleKeyUtil,
  getModuleIcon as getModuleIconUtil,
  getDayKeysBetween as getDayKeysBetweenUtil,
  getRangeForKey as getRangeForKeyUtil,
  formatDate as formatDateUtil,
  getNumericSeries as getNumericSeriesUtil,
  normalizeBarSeries as normalizeBarSeriesUtil,
  extractUserKey as extractUserKeyUtil,
  extractUserName as extractUserNameUtil,
  getModuleLabelFromKey as getModuleLabelFromKeyUtil,
} from '@Company/config/activity-utils';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

export default {
  name: 'ActivityLevelModal',
  components: { ActivitySparkline, apexchart: ApexCharts, DateFilterComponent },
  data(){
    return {
      visible: false,
      loading: false,
      title: 'Company',
      companyId: null,
      raw: null,
      modules: [],
      users: [],
      tab: 0,
      // filters
      rangeKey: 'this_month',
      rangeStart: null,
      rangeEnd: null,
      monthMenu: false,
      monthPicker: null,
         activityModules: [
          { key: 'viewer', label: 'Viewer', icon: 'mdi-eye-outline' },
          { key: 'corrosionAI', label: 'Corrosion AI', icon: 'mdi-robot-outline' },
          { key: 'askAva', label: 'Ask AVA', icon: 'mdi-account-question-outline' },
          { key: 'askAvaDashboards', label: 'Ask AVA Dashboards', icon: 'mdi-view-dashboard-outline' },
          { key: 'inspection', label: 'Inspection Module', icon: 'mdi-magnify-scan' },
          { key: 'inspectionDashboards', label: 'Inspection Dashboards', icon: 'mdi-view-dashboard-outline' },
          { key: 'taskBoards', label: 'Task Boards', icon: 'mdi-view-kanban' },
          { key: 'customDashboards', label: 'Custom Dashboards', icon: 'mdi-view-dashboard-variant-outline' },
          { key: 'scanDownloads', label: 'Scan Downloads', icon: 'mdi-download' },
        ],
    };
  },
  computed: {
    isLargeDateRange() {
      // Check if date range is more than 3 months (approximately 90 days)
      if (!this.rangeStart || !this.rangeEnd) return false;
      const start = new Date(this.rangeStart);
      const end = new Date(this.rangeEnd);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
      
      // Calculate difference in days
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 90; // More than 3 months
    },
    chartCategories() {
      // Use monthly categories for large date ranges, otherwise use daily
      return this.isLargeDateRange ? this.monthCategories : this.dayCategories;
    },
    dayCategories(){
      // Day labels for currently selected range
      if (!this.rangeStart || !this.rangeEnd) return [];
      return this.getDayKeysBetween(this.rangeStart, this.rangeEnd);
    },
    monthCategories() {
      // Month labels for currently selected range (YYYY-MM format)
      if (!this.rangeStart || !this.rangeEnd) return [];
      return this.getMonthKeysBetween(this.rangeStart, this.rangeEnd);
    },
    monthPickerDisplay(){
      // Show YYYY-MM of the current range start
      if (!this.rangeStart) return '';
      const d = new Date(this.rangeStart);
      if (isNaN(d)) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    },
    totalActivityCount(){
      return (this.modules || []).reduce((sum, m) => sum + (Number(m.count) || 0), 0);
    },
    pieSeries(){
      return (this.modules || []).map(m => Number(m.count) || 0);
    },
    pieLabels(){
      return (this.modules || []).map(m => m.label || m.key || 'Module');
    },
    pieChartOptions(){
      return {
        labels: this.pieLabels,
        legend: { show: false, position: 'bottom' },
        dataLabels: { enabled: false },
        tooltip: {
          y: { formatter: (val) => `${Number(val).toFixed(0)}` }
        },
        stroke: { show: false }
      };
    }
  },
  methods: {
    open(companyItem){
      this.companyId = companyItem && (companyItem.id || companyItem._id) || null;
      this.title = (companyItem && companyItem.name) || 'Company';
      this.visible = true;
      // initialize default range and fetch
      this.setRange('this_month');
      this.fetch();
    },
    close(){
      this.visible = false;
    },
    async fetch(){
      if (!this.companyId){
        this.modules = [];
        this.users = [];
        return;
      }
      this.loading = true;
      try {
        const params = {};
        if (this.rangeStart) params.startDate = this.rangeStart;
        if (this.rangeEnd) params.endDate = this.rangeEnd;
        const { data } = await service.getByCompanyId(this.companyId, params);
        this.raw = data || [];
        // Build map from logs then align to predefined activityModules so every module renders
        const byKey = this.buildModulesFromLogs(Array.isArray(this.raw) ? this.raw : []);
        this.modules = this.activityModules.map(am => {
          const norm = this.normalizeModuleKey(am.key);
          const entry = byKey[norm];
          if (entry){
            return { ...entry, key: am.key, label: am.label, icon: am.icon };
          }
          return { key: am.key, label: am.label, icon: am.icon, count: 0, level: 0, _series: this.emptySeries() };
        });
        // Order by count descending
        this.modules.sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));

        // Build per-user aggregations
        this.users = this.buildUsersFromLogs(Array.isArray(this.raw) ? this.raw : []);
        this.users.sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));
      } catch (e){
        debugError('Failed to load activity logs', e);
        this.modules = [];
        this.users = [];
      } finally {
        this.loading = false;
      }
    },
    handleRangeChange(data){
      this.rangeKey = data.rangeKey;
      this.rangeStart = data.rangeStart;
      this.rangeEnd = data.rangeEnd;
      this.fetch();
    },
    handleDateRangeChange(data){
      this.rangeKey = data.rangeKey;
      this.rangeStart = data.rangeStart;
      this.rangeEnd = data.rangeEnd;
      this.fetch();
    },
    setRange(key){
      this.rangeKey = key;
      this.applyRangeKey(key);
    },
    applyRangeKey(key){
      const { start, end } = this.getRangeForKey(key);
      this.rangeStart = this.formatDate(start);
      this.rangeEnd = this.formatDate(end);
      // Keep month picker in sync with the selected start month
      this.monthPicker = `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2, '0')}`;
    },
    setCustomRange(startDate, endDate){
      this.rangeStart = this.formatDate(startDate);
      this.rangeEnd = this.formatDate(endDate);
    },
    buildModulesFromLogs(logs){
      if (!Array.isArray(logs)) return {};
      // Group logs by normalized module key (to match activityModules keys)
      const groups = logs.reduce((acc, item) => {
        // Handle new response format with countsJson field
        if (item && item.countsJson) {
          try {
            const moduleCounts = JSON.parse(item.countsJson);
            if (moduleCounts && moduleCounts.modules) {
              // Process each module in the countsJson
              Object.entries(moduleCounts.modules).forEach(([moduleName, count]) => {
                const normKey = this.normalizeModuleKey(moduleName);
                if (!acc[normKey]) acc[normKey] = [];
                // Create synthetic entries for each count
                for (let i = 0; i < count; i++) {
                  acc[normKey].push({
                    ...item,
                    moduleName,
                    createDate: item.date
                  });
                }
              });
              return acc;
            }
          } catch (e) {
            debugError('Failed to parse countsJson', e);
          }
        }

        // Fall back to original format if countsJson is not available
        const normKey = this.normalizeModuleKey((item && item.moduleName) || 'unknown');
        (acc[normKey] = acc[normKey] || []).push(item);
        return acc;
      }, {});
      
      // Determine if we should use monthly or daily aggregation
      const useMonthlyAggregation = this.isLargeDateRange;
      
      // Generate appropriate keys for selected range and a formatter for date keys
      const timeKeys = useMonthlyAggregation ? 
        this.getMonthKeysBetween(this.rangeStart, this.rangeEnd) : 
        this.getDayKeysBetween(this.rangeStart, this.rangeEnd);
      
      const toTimeKey = (d) => {
        const dt = new Date(d);
        if (isNaN(dt)) return null;
        const y = dt.getFullYear();
        const m = String(dt.getMonth()+1).padStart(2, '0');
        
        if (useMonthlyAggregation) {
          return `${y}-${m}`; // YYYY-MM for monthly
        } else {
          const day = String(dt.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`; // YYYY-MM-DD for daily
        }
      };
      
      const modules = Object.entries(groups).map(([normKey, items]) => {
        // Bucket per time unit (day or month) for selected range
        const buckets = new Map();
        items.forEach(it => {
          const k = toTimeKey(it && it.createDate);
          if (!k) return;
          buckets.set(k, (buckets.get(k) || 0) + 1);
        });
        
        // Build fixed-length series for the selected range
        const series = timeKeys.map(k => buckets.get(k) || 0);
        const cleaned = this.normalizeBarSeries(series, timeKeys.length);
        const count = items.length;
        const max = cleaned.length ? Math.max(...cleaned) : 0;
        const last = cleaned.length ? cleaned[cleaned.length - 1] : 0;
        // Level as a relative activity percent (last vs max)
        const level = max > 0 ? Math.round((last / max) * 100) : 0;
        
        return [normKey, {
          key: normKey,
          label: normKey,
          icon: this.getModuleIcon(normKey),
          count,
          level,
          _series: cleaned,
          _useMonthlyAggregation: useMonthlyAggregation
        }];
      });
      
      // Reduce to object map keyed by normalized key
      return modules.reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});
    },
    // Get all day labels for the selected range (YYYY-MM-DD)
    getDayKeysBetween(start, end){
      return getDayKeysBetweenUtil(start, end);
    },
    // Get all month labels for the selected range (YYYY-MM)
    getMonthKeysBetween(start, end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return [];
      
      const months = [];
      const currentDate = new Date(startDate);
      
      // Set to first day of month
      currentDate.setDate(1);
      
      // Loop through each month in the range
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        months.push(`${year}-${month}`);
        
        // Move to next month
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      
      return months;
    },
    // Convert day key (YYYY-MM-DD) to month key (YYYY-MM)
    dayKeyToMonthKey(dayKey) {
      if (!dayKey || typeof dayKey !== 'string') return null;
      const parts = dayKey.split('-');
      if (parts.length < 3) return dayKey;
      return `${parts[0]}-${parts[1]}`;
    },
    getRangeForKey(key){
      return getRangeForKeyUtil(key);
    },
    formatDate(d){
      return formatDateUtil(d);
    },
    getModuleIcon(key){
      return getModuleIconUtil(key);
    },
    normalizeModuleKey(name){
      return normalizeModuleKeyUtil(name);
    },
    emptySeries(){
      const len = this.dayCategories.length;
      return Array.from({ length: len }, () => 0);
    },
    getSeries(m){
      // Prefer precomputed series from logs
      if (m && Array.isArray(m._series)) return m._series;
      // Fallback: accept various shapes
      const series = m && (m.series || m.values || m.data || m.timeline || m.history) || [];
      return this.normalizeBarSeries(this.getNumericSeries(series), this.dayCategories.length || 0);
    },
    getColor(m){
      // Prefer provided color; fallback by level
      if (m && m.color) return m.color;
      const level = isFinite(m && m.level) ? Number(m.level) : 0;
      if (level >= 75) return '#43a047';
      if (level >= 40) return '#ffb300';
      return '#e53935';
    },
    getNumericSeries(series){
      return getNumericSeriesUtil(series);
    },
    normalizeBarSeries(series, maxLen = 24){
      return normalizeBarSeriesUtil(series, maxLen);
    },
    // ==== Users aggregation helpers ====
    getModuleLabelFromKey(key){
      return getModuleLabelFromKeyUtil(key, this.activityModules);
    },
    extractUserKey(item){
      // Check for new response format first
      if (item && item.userId) {
        return item.userId;
      }
      return extractUserKeyUtil(item);
    },
    extractUserName(item){
      // Check for new response format first
      if (item && item.user) {
        const firstName = item.user.firstName || '';
        const lastName = item.user.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) return fullName;
      }
      return extractUserNameUtil(item);
    },
    buildUsersFromLogs(logs){
      if (!Array.isArray(logs)) return [];
      
      // Determine if we should use monthly or daily aggregation
      const useMonthlyAggregation = this.isLargeDateRange;
      
      // Generate appropriate keys for selected range
      const timeKeys = useMonthlyAggregation ? 
        this.getMonthKeysBetween(this.rangeStart, this.rangeEnd) : 
        this.getDayKeysBetween(this.rangeStart, this.rangeEnd);
      
      const toTimeKey = (d) => {
        const dt = new Date(d);
        if (isNaN(dt)) return null;
        const y = dt.getFullYear();
        const m = String(dt.getMonth()+1).padStart(2, '0');
        
        if (useMonthlyAggregation) {
          return `${y}-${m}`; // YYYY-MM for monthly
        } else {
          const day = String(dt.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`; // YYYY-MM-DD for daily
        }
      };
      
      const map = new Map();
      logs.forEach(it => {
        // Handle new response format with countsJson field
        if (it && it.countsJson) {
          try {
            const moduleCounts = JSON.parse(it.countsJson);
            if (moduleCounts && moduleCounts.modules) {
              const userKey = it.userId || this.extractUserKey(it);
              const userName = (it.user && `${it.user.firstName} ${it.user.lastName}`.trim()) || 
                               this.extractUserName(it) || 
                               userKey;
              const timeKey = toTimeKey(it && it.date);
              
              if (!map.has(userKey)){
                map.set(userKey, {
                  key: userKey,
                  name: userName,
                  _timeBuckets: new Map(),
                  _moduleCounts: new Map(),
                  count: 0,
                  level: 0,
                  _series: [],
                  _useMonthlyAggregation: useMonthlyAggregation
                });
              }
              
              const u = map.get(userKey);
              const totalCount = it.total || Object.values(moduleCounts.modules).reduce((sum, count) => sum + count, 0);
              u.count += totalCount;
              
              if (timeKey) {
                u._timeBuckets.set(timeKey, (u._timeBuckets.get(timeKey) || 0) + totalCount);
              }
              
              // Add module counts
              Object.entries(moduleCounts.modules).forEach(([moduleName, count]) => {
                const normKey = this.normalizeModuleKey(moduleName);
                u._moduleCounts.set(normKey, (u._moduleCounts.get(normKey) || 0) + count);
              });
              
              return;
            }
          } catch (e) {
            debugError('Failed to parse countsJson', e);
          }
        }

        // Fall back to original format if countsJson is not available
        const userKey = this.extractUserKey(it);
        const userName = this.extractUserName(it) || userKey;
        const timeKey = toTimeKey(it && it.createDate);
        const mod = this.normalizeModuleKey((it && it.moduleName) || 'unknown');
        
        if (!map.has(userKey)){
          map.set(userKey, {
            key: userKey,
            name: userName,
            _timeBuckets: new Map(),
            _moduleCounts: new Map(),
            count: 0,
            level: 0,
            _series: [],
            _useMonthlyAggregation: useMonthlyAggregation
          });
        }
        
        const u = map.get(userKey);
        u.count += 1;
        if (timeKey){ u._timeBuckets.set(timeKey, (u._timeBuckets.get(timeKey) || 0) + 1); }
        if (mod){ u._moduleCounts.set(mod, (u._moduleCounts.get(mod) || 0) + 1); }
      });
      
      // finalize
      const result = Array.from(map.values()).map(u => {
        const series = timeKeys.map(k => u._timeBuckets.get(k) || 0);
        const cleaned = this.normalizeBarSeries(series, timeKeys.length);
        const max = cleaned.length ? Math.max(...cleaned) : 0;
        const last = cleaned.length ? cleaned[cleaned.length - 1] : 0;
        const level = max > 0 ? Math.round((last / max) * 100) : 0;
        
        return {
          key: u.key,
          name: u.name,
          count: u.count,
          level,
          _series: cleaned,
          _moduleCounts: Array.from(u._moduleCounts.entries()).reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {}),
          _useMonthlyAggregation: useMonthlyAggregation
        };
      });
      
      return result;
    },
    getUserSeries(u){
      if (u && Array.isArray(u._series)) return u._series;
      return this.emptySeries();
    },
    getUserPieSeries(u){
      if (!u || !u._moduleCounts) return [];
      const order = (this.activityModules || []).map(m => this.normalizeModuleKey(m.key));
      const counts = [];
      const labels = [];
      // include only modules with non-zero count, preserve predefined order
      order.forEach(k => {
        const v = Number(u._moduleCounts[k] || 0);
        if (v > 0){
          counts.push(v);
          labels.push(this.getModuleLabelFromKey(k));
        }
      });
      // if none matched, include any remaining keys
      if (!counts.length){
        Object.entries(u._moduleCounts).forEach(([k, v]) => {
          const n = Number(v || 0);
          if (n > 0){
            counts.push(n);
            labels.push(this.getModuleLabelFromKey(k));
          }
        });
      }
      // cache labels on the user for options
      u._pieLabels = labels;
      return counts;
    },
    getUserPieOptions(u){
      const labels = this.getUserPieLabels(u);
      return {
        labels,
        legend: { show: false, position: 'bottom' },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => `${Number(val).toFixed(0)}` } },
        stroke: { show: false }
      };
    },
    getUserPieLabels(u){
      if (!u || !u._moduleCounts) return [];
      const order = (this.activityModules || []).map(m => this.normalizeModuleKey(m.key));
      const labels = [];
      order.forEach(k => {
        const v = Number(u._moduleCounts[k] || 0);
        if (v > 0){
          labels.push(this.getModuleLabelFromKey(k));
        }
      });
      if (!labels.length){
        Object.entries(u._moduleCounts).forEach(([k, v]) => {
          const n = Number(v || 0);
          if (n > 0){
            labels.push(this.getModuleLabelFromKey(k));
          }
        });
      }
      return labels;
    },
    isFiniteNumber(v){
      return Number.isFinite(Number(v));
    }
  }
}
</script>

<style scoped>
/* Keep header/actions fixed while list scrolls */
</style>
